import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuraxFlags } from '../lib/auraxFlags'; // EQUIPE_DONO_0211
import { useAuth } from '../context/AuthContext';
import { Loader2, ShieldOff } from 'lucide-react';
import { Button } from './ui/button';
import FirstPasswordChange from '../pages/FirstPasswordChange';

function normalizePath(pathname = '') {
  return String(pathname || '').split('?')[0].replace(/\/+$/, '') || '/';
}

/** Primeira rota acessível — cobre todos os módulos conhecidos (evita loop em /settings). */
const MODULE_HOME_PATHS = [
  ['dashboard', '/dashboard'],
  ['chats', '/chats'],
  ['clients', '/gestor-xui/clients'],
  ['bonification', '/bonification'],
  ['meetings', '/reunioes'],
  ['contacts', '/contacts'],
  ['funnel', '/funnel'],
  ['financeiro', '/sales'],
  ['sales_monitor', '/sales'],
  ['checkout_notifications', '/sales'],
  ['resellers', '/gestor-xui/resellers'],
  ['buy_credits', '/buy-credits'],
  ['whatsapp', '/connections'],
  ['team', '/team'],
  ['triggers', '/triggers'],
  ['mass_dispatch', '/recuperacao'],
  ['notification_templates', '/notification-templates'],
  ['reseller_manual', '/materiais'],
  ['reseller_manual_admin', '/admin/materiais'],
  ['credit_packs', '/gestor-xui/credit-packs'],
  ['reseller_billing', '/gestor-xui/my-reseller-billing'],
  ['gateway', '/gestor-xui/gateway'],
  ['plan_pricing', '/gestor-xui/categories'],
  ['credential_generation', '/gestor-xui/credential-generation'],
  ['integrations', '/gestor-xui/servers'],
  ['permissions', '/permissions'],
  ['logs_globais', '/logs-globais'],
  ['ranking', '/ranking'],
  ['settings_password_only', '/settings'],
  ['settings', '/settings'],
];

function resolveFallbackPath(can) {
  for (const [mod, path] of MODULE_HOME_PATHS) {
    if (can(mod)) return path;
  }
  return null;
}

function NoModuleAccess() {
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-6 py-12 text-center">
      <div className="office-square-icon flex h-12 w-12 items-center justify-center rounded-[3px] bg-muted text-muted-foreground">
        <ShieldOff className="h-6 w-6" aria-hidden />
      </div>
      <div className="max-w-md space-y-2">
        <h1 className="text-lg font-semibold text-foreground">Sem acesso a esta área</h1>
        <p className="text-sm text-muted-foreground">
          Seu grupo de permissões não inclui este módulo. Peça ao administrador para liberar o acesso
          ou escolha outra página no menu lateral.
        </p>
      </div>
      <Button type="button" variant="outline" className="h-9" onClick={() => window.location.assign('/login')}>
        Trocar de conta
      </Button>
    </div>
  );
}

function denyModuleAccess(can, pathNow) {
  const fallbackPath = resolveFallbackPath(can);
  if (!fallbackPath) return <NoModuleAccess />;
  const fb = normalizePath(fallbackPath);
  if (fb !== pathNow && !pathNow.startsWith(`${fb}/`)) {
    return <Navigate to={fallbackPath} replace />;
  }
  return <NoModuleAccess />;
}

const ProtectedRoute = ({ children }) => {
  const { user, loading, accountSummary } = useAuth();
  const location = useLocation();
  const eqFlags0211 = useAuraxFlags(); // EQUIPE_DONO_0211

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user.password_change_required) return <FirstPasswordChange />;

  const isAttendant = user?.role === 'attendant';
  const modules = Array.isArray(accountSummary?.modules) ? accountSummary.modules : [];
  const canMod = (k) => modules.includes('*') || modules.includes(k);

  // Rotas exclusivas do administrador — atendentes nunca acessam (mesmo com módulo legado).
  if (isAttendant) {
    const p = location.pathname || '';
    if (
      p.startsWith('/logs-globais')
      || p.startsWith('/bonification')
      || p.startsWith('/ranking')
      || p.startsWith('/gestor-xui/credential-generation')
    ) {
      const home = resolveFallbackPath(canMod) || '/chats';
      return <Navigate to={home} replace />;
    }
  }

  // Conta inativa (só recarga): força a compra de créditos.
  if (user?.role === 'reseller' && accountSummary?.recharge_only && eqFlags0211 == null) {
    return (<div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>);
  }
  if (user?.role === 'reseller' && accountSummary?.recharge_only && !eqFlags0211?.equipe_dono_0211) {
    const p = location.pathname || '';
    const dest = '/buy-credits';
    const allowed = p.startsWith('/buy-credits') || p === '/comunicados';
    if (!allowed) {
      return <Navigate to={dest} replace />;
    }
  }

  // Admin não compra créditos: apenas configura pacotes e Modo crédito.
  if (user?.role === 'admin') {
    const p = location.pathname || '';
    if (p.startsWith('/buy-credits') || p.startsWith('/postpaid-wallet')) {
      return <Navigate to="/gestor-xui/credit-packs" replace />;
    }
  }

  // Admin usa configuração global; revenda usa rota da sub-rede.
  if (user?.role === 'admin') {
    const p = location.pathname || '';
    if (p.startsWith('/gestor-xui/my-reseller-billing')) {
      return <Navigate to="/gestor-xui/reseller-billing" replace />;
    }
  }

  // Guard por módulos (backend também valida)
  if (user?.role !== 'admin' && modules.length > 0) {
    const can = canMod;
    const p = location.pathname || '';
    const pathNow = normalizePath(p);

    const required =
      p.startsWith('/dashboard') ? 'dashboard'
      : p.startsWith('/chats') ? 'chats'
      : p.startsWith('/bonification') ? 'bonification'
      : p.startsWith('/reunioes') ? 'meetings'
      : p.startsWith('/contacts') ? 'contacts'
      : p.startsWith('/funnel') ? 'funnel'
      : p.startsWith('/triggers') ? 'triggers'
      : p.startsWith('/logs-financeiros') ? 'financeiro'
      : p.startsWith('/sales') ? 'financeiro|sales_monitor|checkout_notifications'
      : p.startsWith('/attendant/metrics') ? 'dashboard'
      : p.startsWith('/logs-globais') ? 'logs_globais'
      : p.startsWith('/ranking') ? (user?.role === 'reseller' ? null : 'ranking')
      : p.startsWith('/connections') ? 'whatsapp'
      : p.startsWith('/integrations') ? 'integrations'
      : p.startsWith('/gestor-xui/servers') ? 'integrations'
      : p.startsWith('/permissions') ? 'permissions'
      : p.startsWith('/settings') ? 'settings'
      : p.startsWith('/team') ? (eqFlags0211?.equipe_0211 ? null : 'team') /* EQUIPE_DONO_0211 */
      : p.startsWith('/buy-credits') ? 'buy_credits'
      : p.startsWith('/postpaid-wallet') ? 'buy_credits'
      : p.startsWith('/app-activations') ? (user?.role === 'attendant' ? 'app_activations_manage' : 'clients')
      : p.startsWith('/gestor-xui/clients') ? 'clients'
      : p.startsWith('/gestor-xui/own-player') ? 'clients'
      : p.startsWith('/gestor-xui/live-clients') ? 'clients'
      : p.startsWith('/gestor-xui/tests') ? 'clients'
      : p.startsWith('/gestor-xui/categories') ? 'clients|plan_pricing'
      : p.startsWith('/gestor-xui/credential-generation') ? 'credential_generation'
      : p.startsWith('/gestor-xui/plan-pricing') ? 'plan_pricing'
      : p.startsWith('/gestor-xui/credit-packs') ? 'credit_packs'
      : p.startsWith('/gestor-xui/reseller-billing') ? 'credit_packs'
      : p.startsWith('/gestor-xui/my-reseller-billing') ? 'reseller_billing'
      : p.startsWith('/gestor-xui/gateway') ? 'gateway'
      : p.startsWith('/gestor-xui/packages') ? 'plan_pricing'
      : p.startsWith('/gestor-xui/checkout-notifications') ? 'checkout_notifications'
      : p.startsWith('/gestor-xui/my-portfolio') ? 'resellers'
      : p.startsWith('/gestor-xui/inactive-resellers') ? 'resellers'
      : p.startsWith('/gestor-xui/reseller-migration') ? 'resellers'
      : p.startsWith('/gestor-xui/resellers') ? 'resellers'
      : p.startsWith('/gestor-xui/payments') ? 'financeiro'
      : p.startsWith('/gestor-xui/sales') ? 'sales_monitor'
      : p.startsWith('/gestor-xui/credit-logs') ? 'sales_monitor'
      : p.startsWith('/notification-templates') ? 'notification_templates'
      : p.startsWith('/materiais') || p.startsWith('/reseller-manual') ? 'reseller_manual'
      : p.startsWith('/admin/materiais') || p.startsWith('/admin/reseller-manual') ? 'reseller_manual_admin'
      : null;

    if (user?.role === 'attendant' && (p.startsWith('/gestor-xui/my-portfolio') || (p.startsWith('/app-activations') && user?.managed_by_reseller_id))) {
      return <Navigate to="/chats" replace />;
    }

    if (required === 'settings') {
      // Compatibilidade: algumas contas só podem alterar senha.
      if (!can('settings') && !can('settings_password_only')) {
        return denyModuleAccess(can, pathNow);
      }
    } else if (required) {
      const keys = String(required).includes('|') ? String(required).split('|') : [required];
      if (!keys.some((k) => can(k))) {
        return denyModuleAccess(can, pathNow);
      }
    }
  }

  return children ?? <Outlet />;
};

export default ProtectedRoute;
